import { Persistence } from "../config/constants";
import { BaseEntity } from "../core/entity/BaseEntity";
import { Entity } from "../core/entity/Entity";
import { ConsumableId } from "../game/Consumable";
import { PayoutEvent } from "../game/PayoutEvent";
import { RewardTarget, supplyTarget } from "./hud/payout/rewardTargets";
import { ShellStore } from "./ShellStore";

/**
 * The reward flights in the air: every chip the DOM's `RewardFlightLayer`
 * is flying to a readout, and where each one took off from. Anything
 * that grants something the HUD tallies launches one — the sale
 * celebration bursts from the middle of the screen, a nail pried on the
 * bench takes off from the nail's own hole — and the layer draws
 * whatever is here.
 *
 * Shell-side state, never serialized: the numbers a flight carries
 * settled in the sim the instant they were granted, and the flight is
 * decoration over them. The one thing the sim's numbers don't decide is
 * what the tally *shows*: a supply still in the air is held back from
 * its readout until it lands (`supplyInFlight`), so the count ticks up
 * when the chip arrives instead of a beat before it takes off — a
 * presentation buffer, in the same spirit as `PayoutBuffer`.
 */

/** What a chip is drawn as — the layer keeps the art (see StarIcon). */
export type RewardGlyph = "coin" | "star" | "spark" | "nail";

export interface RewardChip {
  readonly id: string;
  readonly target: RewardTarget;
  /** Where to land when `target` isn't on screen. */
  readonly fallbackTarget?: RewardTarget;
  readonly glyph: RewardGlyph;
  /** How long after launch the chip leaves — a burst's stagger, or the
   * beat a pull takes before the nail is clear of the wood. */
  readonly delayMs: number;
  /** Spread around the flight's origin so a burst doesn't fly as one blob. */
  readonly offsetX: number;
  readonly offsetY: number;
}

export interface RewardFlight {
  readonly id: string;
  /** Where the chips take off from, in window pixels. */
  readonly originX: number;
  readonly originY: number;
  readonly chips: ReadonlyArray<RewardChip>;
  /** The supply the flight is carrying to the tally, if any — the tally
   * shows it only once the flight has landed. */
  readonly supply?: { readonly id: ConsumableId; readonly amount: number };
}

/** How much of one supply is still in the air: launched, not yet landed. */
export function supplyInFlight(
  flights: Iterable<RewardFlight>,
  landed: ReadonlySet<string>,
  id: ConsumableId,
): number {
  let held = 0;
  for (const flight of flights) {
    if (landed.has(flight.id)) continue;
    if (flight.supply?.id === id) held += flight.supply.amount;
  }
  return held;
}

/** How many coins one payout throws. Bigger paydays throw more. */
export function coinCount(money: number): number {
  if (money <= 0) return 0;
  return Math.min(12, 3 + Math.floor(Math.log10(Math.max(money, 1)) * 3));
}

const COIN_STAGGER_MS = 55;

/** A sale's celebration: coins fanned out as a burst, then the star and
 * the spark, each to the readout that tracks it. */
export function payoutChips(payout: PayoutEvent): RewardChip[] {
  const chips: RewardChip[] = [];
  const coins = coinCount(payout.money);
  for (let i = 0; i < coins; i++) {
    // Fan the coins around a circle so they leave the origin as a burst.
    const angle = (i / Math.max(coins, 1)) * Math.PI * 2;
    chips.push({
      id: `${payout.id}-coin-${i}`,
      target: "money",
      glyph: "coin",
      delayMs: i * COIN_STAGGER_MS,
      offsetX: Math.cos(angle) * 26,
      offsetY: Math.sin(angle) * 26,
    });
  }
  if (payout.reputation > 0) {
    chips.push({
      id: `${payout.id}-rep`,
      target: "reputation",
      glyph: "star",
      delayMs: coins * COIN_STAGGER_MS + 90,
      offsetX: -30,
      offsetY: 8,
    });
  }
  if (payout.xp > 0) {
    chips.push({
      id: `${payout.id}-xp`,
      target: "xp",
      glyph: "spark",
      delayMs: coins * COIN_STAGGER_MS + 200,
      offsetX: 30,
      offsetY: 8,
    });
  }
  return chips;
}

/** One unit of a supply on its way to the tally — to its own row when
 * the tally is open, to the word "Supplies" when it's folded. */
export function supplyChip(
  flightId: string,
  id: ConsumableId,
  glyph: RewardGlyph,
  delayMs: number,
): RewardChip {
  return {
    id: `${flightId}-${id}`,
    target: supplyTarget(id),
    fallbackTarget: "supplies",
    glyph,
    delayMs,
    offsetX: 0,
    offsetY: 0,
  };
}

export class RewardFlights extends BaseEntity implements Entity {
  id = "rewardFlights";
  persistenceLevel: number = Persistence.Permanent;
  pausable = false;

  private flights: RewardFlight[] = [];
  /** Flights whose first chip has arrived — what they carried is on the
   * readout now, even while the rest of the burst is still landing. */
  private landed = new Set<string>();

  /** Every flight launched and not yet finished, in launch order. */
  get all(): ReadonlyArray<RewardFlight> {
    return this.flights;
  }

  launch(flight: RewardFlight): void {
    this.flights = [...this.flights, flight];
    this.bump();
  }

  /** The first chip is down: whatever the flight carried has arrived. */
  land(flightId: string): void {
    if (this.landed.has(flightId)) return;
    this.landed.add(flightId);
    this.bump();
  }

  /** The last chip is down and faded: the flight is over. */
  finish(flightId: string): void {
    if (!this.flights.some((flight) => flight.id === flightId)) return;
    this.flights = this.flights.filter((flight) => flight.id !== flightId);
    this.landed.delete(flightId);
    this.bump();
  }

  /** How much of one supply the tally should hold back for now. */
  supplyInFlight(id: ConsumableId): number {
    return supplyInFlight(this.flights, this.landed, id);
  }

  private bump(): void {
    // Flights aren't in the ShellStore's signature (shell-side state);
    // announce every change ourselves so the layer and the tally redraw.
    this.game.entities.tryGetSingleton(ShellStore)?.bump();
  }
}
